import {
  FIN_CATEGORY_BY_KEY,
  type FinGroupName,
  FinGroups,
  getCategoryExperience,
} from '#dictionary'
import { formatKeyValue } from '#formatting'

import createTimelineRecord from './createTimelineRecord'
import type { TimelineRecord } from './TimelineRecord'

// Statutory double-entry accounting sign. Positive-flow groups (Income,
// Assets, Coverage, Liabilities, Technical) carry +1; groups that
// subtract from the household's position (Expenses, Contributions,
// Taxes) carry -1. Liabilities is +1 because the balance is stored as a
// positive dollar amount — the "owed reduces net worth" arithmetic is
// applied at the ledger level, not the group sign.
const NEGATIVE_FLOW_GROUPS = new Set<FinGroupName>([
  FinGroups.Expenses,
  FinGroups.Contributions,
  FinGroups.Taxes,
])

const doubleEntryMultiplier = (finGroup: FinGroupName): 1 | -1 =>
  NEGATIVE_FLOW_GROUPS.has(finGroup) ? -1 : 1

export const generateDoubleEntryRecord = (
  record: TimelineRecord
): TimelineRecord | null => {
  if (record.doubleEntryFromRecordId) {
    return null
  }

  const firstEntryCategory = FIN_CATEGORY_BY_KEY.get(record.categoryKey)

  if (!firstEntryCategory || !firstEntryCategory.doubleEntryCategoryKey) {
    return null
  }

  const doubleEntryCategory = FIN_CATEGORY_BY_KEY.get(
    firstEntryCategory.doubleEntryCategoryKey
  )

  if (!doubleEntryCategory) {
    return null
  }

  const finGroup = getCategoryExperience(firstEntryCategory).finGroup
  const value = record.value * doubleEntryMultiplier(finGroup)
  const description = formatKeyValue('Double entry', [
    ['First Entry', firstEntryCategory.name],
    ['Double Entry', doubleEntryCategory.name],
  ])

  return createTimelineRecord({
    year: record.year,
    value,
    description,
    categoryKey: doubleEntryCategory.key,
    financialRecord: record.financialRecord,
    doubleEntryFromRecordId: record.id,
    intent: 'DoubleEntry',
  })
}

const generateDoubleEntryRecords = (
  timeline: TimelineRecord[]
): TimelineRecord[] =>
  timeline.map(generateDoubleEntryRecord).filter(record => record !== null)

export default generateDoubleEntryRecords
